import {
  AutocompleteV2,
  AutocompleteV2Props,
  ListItemText,
  Stack,
  Typography,
} from '@cast/design-system';
import { InstanceIcon } from 'features/db-optimization/v2/components/InstanceIcon';
import { dboProtocolToLabel } from 'features/db-optimization/v2/constants';
import { InstanceViewModelV2 } from 'features/db-optimization/v2/types';

type DbServerSelectProps = {
  instances: InstanceViewModelV2[];
} & Omit<
  AutocompleteV2Props<InstanceViewModelV2>,
  | 'getStringValue'
  | 'getLabel'
  | 'groupBy'
  | 'renderMenuItemGroupLabel'
  | 'items'
  | 'addCustomItem'
  | 'addCustomItemHint'
>;

const MenuItem = ({ name, protocol, provider }: InstanceViewModelV2) => {
  return (
    <Stack direction="row" gap={8}>
      <InstanceIcon
        provider={provider}
        size={16}
        sx={{ alignSelf: 'center' }}
      />
      <ListItemText
        primary={name}
        primaryTypographyProps={{ variant: 'P12M' }}
        secondary={dboProtocolToLabel[protocol]}
        secondaryTypographyProps={{ sx: { textTransform: 'uppercase' } }}
      />
    </Stack>
  );
};

const SelectedItem = ({ name, protocol, provider }: InstanceViewModelV2) => {
  return (
    <Stack direction="row" gap="8px">
      <InstanceIcon
        provider={provider}
        size={16}
        sx={{ alignSelf: 'center' }}
      />
      <Stack overflow="hidden" justifyContent="center">
        <Typography variant="P12M" lineHeight="16px" noWrap>
          {name}
        </Typography>
        <Typography
          variant="L10R"
          lineHeight="12px"
          color="textSecondary"
          noWrap
        >
          {dboProtocolToLabel[protocol]}
        </Typography>
      </Stack>
    </Stack>
  );
};

export const DbServerSelect = ({
  instances,
  value,
  onChange,
  ...restProps
}: DbServerSelectProps) => {
  return (
    <AutocompleteV2
      items={instances}
      value={value}
      onChange={onChange}
      filterItems={({ searchInputValue, items }) => {
        const queryLowercase = searchInputValue.toLowerCase();
        return items.filter(
          ({ name, id }) =>
            name.toLowerCase().includes(queryLowercase) ||
            (id?.toLowerCase().includes(queryLowercase) ?? false)
        );
      }}
      renderMenuItem={({ item }) => <MenuItem {...item} />}
      renderSelectedItem={({ item }) => <SelectedItem {...item} />}
      getLabel={({ name }) => name}
      getStringValue={({ id }) => id ?? ''}
      {...restProps}
    />
  );
};
